import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, Link as RouterLink } from "react-router-dom";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import SearchIcon from "@material-ui/icons/Search";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";
import Link from "@material-ui/core/Link";
import InputAdornment from "@material-ui/core/InputAdornment";
import GolfCourseIcon from '@material-ui/icons/GolfCourse';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import MyLocationIcon from '@material-ui/icons/MyLocation';
import ThumbUpAltOutlined from '@material-ui/icons/ThumbUpAltOutlined';
import ReactGA from 'react-ga4';

import { getCourseByName, getCourseById, getMostLikedCourses, getCoursesNearby } from "../../actions/courses";
import Course from "./Course/Course";
import { CourseListSkeleton } from "./CourseSkeleton";
import { isEmpty } from "../../helper";
import { RESET_COURSES } from "../../constants/actionTypes";
import { FEATURED_COURSE_ID } from "../../constants/index";
import { STATES } from "../../constants/states";
import useStyles from "./styles";
import GoogleAd from "../GoogleAd/GoogleAd";

const Courses = () => {
  const dispatch = useDispatch();
  const [courseName, setCourseName] = useState("");
  const [mostLikedClick, setMostLikedClick] = useState(false);
  const [nearMeLoading, setNearMeLoading] = useState(false);
  const [nearMeActive, setNearMeActive] = useState(false);
  const [searchPending, setSearchPending] = useState(false);
  const debounceRef = useRef(null);
  const { course, courses, isLoading } = useSelector((state) => state.courses);
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();

  const searchCourse = (e) => {
    const name = e.target.value;
    ReactGA.event({
      category: 'Course',
      action: 'Course search input',
      label: name
    });
    setCourseName(name);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (name.length === 0) {
      setSearchPending(false);
      dispatch({ type: RESET_COURSES, payload: [] });
      history.replace('/courses/search');
      return;
    }
    setSearchPending(true);
    debounceRef.current = setTimeout(() => {
      setSearchPending(false);
      dispatch(getCourseByName(name));
      history.replace(`/courses/search?q=${encodeURIComponent(name)}`);
    }, 300);
  };

  const mostLiked = (e) => {
    if (!mostLikedClick) {
      setMostLikedClick(true);
      setNearMeActive(false);
      dispatch(getMostLikedCourses());
      history.replace('/courses/search');
    } else {
      setMostLikedClick(false);
      dispatch({ type: RESET_COURSES, payload: [] });
      history.replace('/courses/search');
    }
  };

  const handleNearMe = () => {
    if (nearMeActive) {
      setNearMeActive(false);
      dispatch({ type: RESET_COURSES, payload: [] });
      history.replace('/courses/search');
      return;
    }
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }
    setNearMeLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setNearMeActive(true);
        setMostLikedClick(false);
        dispatch(getCoursesNearby(latitude, longitude));
        setNearMeLoading(false);
        history.replace('/courses/search');
      },
      () => {
        alert("Unable to retrieve your location. Please allow location access and try again.");
        setNearMeLoading(false);
      }
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(getCourseById(FEATURED_COURSE_ID));
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    if (q) {
      setCourseName(q);
      dispatch(getCourseByName(q));
    } else {
      dispatch({ type: RESET_COURSES, payload: [] });
    }
  }, [dispatch]);

  const handleKeyPress = (e) => {
    if (e.keyCode === 13 && courseName.length > 0) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setSearchPending(false);
      dispatch(getCourseByName(courseName));
      history.replace(`/courses/search?q=${encodeURIComponent(courseName)}`);
    }
  };

  const hasActiveSearch = courseName.length > 0 || nearMeActive || mostLikedClick;
  // Only exclude the featured course from results on the default view,
  // where it's already displayed as the featured card. During an active
  // search it must appear in results (e.g. searching "wing park").
  const resultCourses = (Array.isArray(courses) ? courses : []).filter(
    (c) => hasActiveSearch || c._id !== FEATURED_COURSE_ID
  );

  return (
    <>
      <Helmet>
        <title>Search Golf Courses | 20,000+ Courses | RYZE Golf</title>
        <meta name="description" content="Search over 20,000 golf courses worldwide. Find scorecards, tee boxes, slope ratings, course ratings, and locations on RYZE Golf." />
        <link rel="canonical" href="https://ryzegolf.com/courses/search" />
        <meta property="og:title" content="Search Golf Courses | RYZE Golf" />
        <meta property="og:description" content="Search over 20,000 golf courses worldwide. Find scorecards, tee boxes, ratings, and locations on RYZE Golf." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ryzegolf.com/courses/search" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "RYZE Golf",
          "url": "https://ryzegolf.com",
          "description": "Search over 20,000 golf courses worldwide. Find scorecards, tee boxes, slope ratings, course ratings, and locations.",
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": "https://ryzegolf.com/courses/search?q={search_term_string}"
            },
            "query-input": "required name=search_term_string"
          }
        })}</script>
      </Helmet>

      {/* ── Hero + Search ─────────────────────────────────── */}
      <Box className={classes.searchHero} component="section">
        <Typography className={classes.searchHeroEyebrow}>Course Database</Typography>
        <Typography variant="h3" component="h1" className={classes.searchHeroTitle}>
          Find Any Golf Course
        </Typography>
        <Typography className={classes.searchHeroSubtitle}>
          Search 20,000+ courses worldwide — full scorecards, tee yardages, slope and
          course ratings, maps, and community reviews.
        </Typography>
        <TextField
          InputProps={{
            classes: {
              root: classes.root,
              notchedOutline: classes.notchedOutline,
            },
            startAdornment: (
              <InputAdornment position="start" className={classes.searchIcon}>
                <GolfCourseIcon className={classes.iconHandler} />
              </InputAdornment>
            ),
          }}
          variant="outlined"
          color="primary"
          className={classes.searchHeroInput}
          placeholder="Search by course name..."
          onKeyDown={handleKeyPress}
          value={courseName}
          onChange={(e) => {
            searchCourse(e);
          }}
          inputProps={{ 'aria-label': 'Search golf courses by name' }}
        />
        <div className={classes.quickActions}>
          <Chip
            clickable
            icon={nearMeLoading
              ? <CircularProgress size={16} color="inherit" style={{ marginLeft: 8 }} />
              : <MyLocationIcon style={{ fontSize: '1.05rem' }} />}
            label="Courses Near Me"
            onClick={handleNearMe}
            className={`${classes.quickActionChip} ${nearMeActive ? classes.quickActionChipActive : ''}`}
          />
          <Chip
            clickable
            icon={<ThumbUpAltOutlined style={{ fontSize: '1.05rem' }} />}
            label="Most Liked"
            onClick={mostLiked}
            className={`${classes.quickActionChip} ${mostLikedClick ? classes.quickActionChipActive : ''}`}
          />
          <Chip
            clickable
            icon={<AddCircleOutlineIcon style={{ fontSize: '1.05rem' }} />}
            label="Add a Course"
            onClick={() => history.push('/courses/add')}
            className={classes.quickActionChip}
          />
        </div>
      </Box>

      {/* ── Results ───────────────────────────────────────── */}
      {isLoading && courses.length < 1 ? (
        <CourseListSkeleton count={5} />
      ) : (
        <>
          {hasActiveSearch && resultCourses.length > 0 && (
            <Typography className={classes.resultsMeta}>
              {resultCourses.length} course{resultCourses.length === 1 ? '' : 's'} found
              {courseName.length > 0 ? ` for "${courseName}"` : nearMeActive ? ' near you' : ''}
            </Typography>
          )}
          {/* Featured course shown on the default view */}
          {!hasActiveSearch && !isEmpty(course) && <Course course={course} featuredCourse={true} />}
          {/* Ad: between featured course and search results */}
          {/* {courses.length > 0 && <GoogleAd slot="2389072457" format="fluid" paper />} */}
          {/* No results found for an active search */}
          {!isLoading && !searchPending && courses.length === 0 && hasActiveSearch && (
            <div className={classes.noCoursesFound}>
              <div style={{ textAlign: 'center' }}>
                <SearchIcon style={{ fontSize: 48, opacity: 0.4 }} />
                <Typography variant="h6" gutterBottom>
                  No courses found{courseName.length > 0 ? ` for "${courseName}"` : ''}
                </Typography>
                <Typography color="textSecondary">
                  Try a different spelling, a shorter name, or search by city. Can't find your course?{' '}
                  <span
                    role="button"
                    tabIndex={0}
                    className={classes.courseLink}
                    onClick={() => history.push('/courses/add')}
                    onKeyDown={(e) => e.key === 'Enter' && history.push('/courses/add')}
                  >
                    Add it to the database
                  </span>.
                </Typography>
              </div>
            </div>
          )}
          {/* Courses Returned By Search */}
          {resultCourses.map((course) => (
            <Course key={course._id} course={course} featuredCourse={false} />
          ))}
        </>
      )}

      {/* ── Discovery content (default view only) ─────────── */}
      {!hasActiveSearch && (
        <>
          <Divider className={classes.searchDivider} />

          <Box component="section">
            <Typography className={classes.sectionLabel}>Browse by Location</Typography>
            <Typography variant="h2" className={classes.sectionTitle}>
              Best Golf Courses by State
            </Typography>
            <Typography className={classes.sectionSubtitle}>
              Not sure where to start? Explore our curated rankings of the best public and
              resort courses in every state.
            </Typography>
            <Grid container spacing={2}>
              {STATES.map(({ slug, label }) => (
                <Grid item xs={6} sm={4} md={3} key={slug}>
                  <RouterLink to={`/best-golf-courses/${slug}`} className={classes.stateItem}>
                    <span>{label}</span>
                    <span className={classes.stateArrow}>→</span>
                  </RouterLink>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box className={classes.aboutBlock} component="section">
            <Typography className={classes.sectionLabel}>About the Database</Typography>
            <Typography variant="h2" className={classes.sectionTitle}>
              How RYZE Course Data Works
            </Typography>
            <Typography paragraph className={classes.aboutBlockText}>
              Every course page includes the full scorecard with hole-by-hole pars, handicaps,
              and yardages for each tee box, plus USGA slope and course ratings where available.
              Course data comes from public records and is continuously improved by golfer
              submissions — if you spot something outdated, you can propose an update directly
              on any course page.
            </Typography>
            <Typography className={classes.aboutBlockText}>
              Want to put the data to use? Calculate your playing handicap for any set of tees
              with the{' '}
              <Link component={RouterLink} to="/handicap-calculator" className={classes.courseLink}>
                handicap calculator
              </Link>
              , brush up on terms in the{' '}
              <Link component={RouterLink} to="/glossary" className={classes.courseLink}>
                golf glossary
              </Link>
              , or dig into our{' '}
              <Link component={RouterLink} to="/golf-tips" className={classes.courseLink}>
                data-backed golf guides
              </Link>
              .
            </Typography>
          </Box>
        </>
      )}
    </>
  );
};

export default Courses;
